import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { applySeo } from "@/lib/seo";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
  PieChart, Pie, LineChart, Line, ReferenceDot, LabelList,
} from "recharts";
import { ArrowUpRight, AlertCircle, ShieldAlert } from "lucide-react";

// ---------------------------------------------------------------------------
// Datenblock — FNG-Marktbericht Nachhaltige Geldanlagen 2026
// Stichtag 31.12.2025, Erhebung im 1. Quartal 2026, Umfrage bei 77 Instituten
// (davon 57 in Deutschland). Quelle: forum-ng.org, Cash., Vividam.
// ---------------------------------------------------------------------------

// (1) Zusammensetzung der erfassten nachhaltigen Geldanlagen Deutschland 2025
// Anteile sind aus den Volumina berechnet (kein FNG-Wert).
const COMPOSITION_2025 = [
  { name: "Selbstverwaltetes Vermögen der Asset Owner", short: "Asset Owner (selbstverwaltet)", vol: 156.8, share: 26.2, color: "#3E5743" },
  { name: "Nachhaltige Publikumsfonds", short: "Publikumsfonds", vol: 154.0, share: 25.7, color: "#5C7A4E" },
  { name: "Mandate und Spezialfonds", short: "Mandate & Spezialfonds", vol: 127.2, share: 21.3, color: "#7A8A5C" },
  { name: "Eigenanlagen der Banken", short: "Eigenanlagen Banken", vol: 125.7, share: 21.0, color: "#A99968" },
  { name: "Kundeneinlagen der Banken", short: "Kundeneinlagen Banken", vol: 34.8, share: 5.8, color: "#C4A980" },
];
const TOTAL_2025 = 598.5;
const ADDITIONAL_CREDITS = 113; // Mrd. €, nicht in 598,5 enthalten
const TOTAL_INCL_CREDITS = 712;

// (4) Ausschlüsse Deutschland 2024 -> 2025
const EXCLUSIONS = [
  { key: "Sämtliche Waffen", y2024: 72, y2025: 62 },
  { key: "ABC-Waffen", y2024: 99, y2025: 91 },
  { key: "Streubomben / Anti-Personen-Minen", y2024: 97, y2025: 97 },
  { key: "Korruption / Bestechung", y2024: 81, y2025: 93 },
  { key: "Verstöße gegen Menschenrechte", y2024: null, y2025: 97 },
];

// (5) Angewandte Anlagestrategien Deutschland 2025
const STRATEGIES = [
  { key: "Ausschlüsse", value: 100 },
  { key: "Normbasiertes Screening", value: 88 },
  { key: "Engagement", value: 80 },
  { key: "Stimmrechtsausübung", value: 58 },
  { key: "Best-in-Class", value: 17 },
  { key: "Nachhaltige Themenfonds", value: 8 },
  { key: "Impact Investing", value: 5 },
];

// (6) Asset-Allokation der erfassten Anlagen Deutschland 2025
const ASSET_ALLOC = [
  { key: "Staatsanleihen", value: 32, color: "#3E5743" },
  { key: "Immobilien", value: 19, color: "#5C7A4E" },
  { key: "Unternehmensanleihen", value: 19, color: "#7A8A5C" },
  { key: "Sonstige", value: 18, color: "#A99968" },
  { key: "Aktien", value: 12, color: "#C4A980" },
];

// (7) Branchenstimmung (n=73)
const OFFER_CHANGE = [
  { key: "Angebot 2025 ergänzt", value: 29 },
  { key: "Unverändert", value: 70 },
  { key: "Angebot reduziert", value: 1 },
];
const GROWTH_EXPECT_2026 = [
  { key: "Stagnation", value: 53 },
  { key: "Wachstum bis +10 %", value: 36 },
  { key: "Rückgang bis −10 %", value: 8 },
  { key: "Rückgang über −10 %", value: 1 },
  { key: "Wachstum +11 bis +20 %", value: 1 },
];

// (9) Morningstar-Zahlen (Sekundärquelle: Vividam/Cash)
const MORNINGSTAR = [
  { year: "2024", value: 38 },
  { year: "2025", value: -84 },
];

// Historische FNG-Werte 2019–2024 (frühere Erhebungen, andere Stichprobe)
const HIST_TIMESERIES = [
  { year: 2019, vol: 269 },
  { year: 2020, vol: 335 },
  { year: 2021, vol: 501 },
  { year: 2022, vol: 578 },
  { year: 2023, vol: 543 },
  { year: 2024, vol: 672.7 },
];

const fmtVol = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtPct = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " %";

const FAQ = [
  {
    q: "Was ist der FNG-Marktbericht?",
    a: "Das Forum Nachhaltige Geldanlagen e. V. (FNG) veröffentlicht jährlich einen Marktbericht zu nachhaltigen Geldanlagen in Deutschland und Österreich. Für den Bericht 2026 wurden 77 Institute befragt, davon 57 in Deutschland. Der Bericht wurde am 17.06.2026 veröffentlicht, Stichtag der Daten ist der 31.12.2025.",
  },
  {
    q: "Warum sind die Zahlen 2025 nicht mit 2024 vergleichbar?",
    a: "Weil sich die Teilnehmergruppe der jährlichen Erhebung ändert. Ein Institut, das in einem Jahr geantwortet hat, kann im nächsten fehlen – und umgekehrt. Ein direkter Vergleich von 598,5 Mrd. € (2025) und 672,7 Mrd. € (2024) wäre deshalb methodisch nicht sauber.",
  },
  {
    q: "Sind Artikel-9-Fonds automatisch nachhaltig?",
    a: "Nein. Die Kategorien Artikel 8 und Artikel 9 stammen aus der EU-Offenlegungsverordnung (SFDR) und beschreiben, welche Nachhaltigkeitsmerkmale beziehungsweise -ziele ein Fonds ausweist. Sie sagen wenig über die tatsächliche Wirkung. In den erfassten deutschen Publikumsfonds sind 94 % Artikel 8 und 6 % Artikel 9 klassifiziert.",
  },
  {
    q: "Wie groß ist der gesamte Artikel-8/9-Markt in Deutschland?",
    a: "Laut BVI liegen die Artikel-8- und Artikel-9-Fonds in Deutschland im zweiten Halbjahr 2025 bei rund 1.253 Mrd. €. Die FNG-Stichprobe deckt davon etwa ein Fünftel ab (273,5 Mrd. € in Artikel-8- und -9-Fonds).",
  },
  {
    q: "Was ändert sich mit SFDR 2.0?",
    a: "Die Reform ersetzt die bisherigen Artikel-8- und Artikel-9-Bezeichnungen durch drei neue Kategorien: ‚Transition‘, ‚ESG-Basic‘ und ‚Sustainable‘. Eine erste Schätzung geht davon aus, dass rund zwei Drittel der bestehenden ESG-Fonds in die Kategorie ‚ESG-Basic‘ fallen könnten. Mehr als 130 Unterzeichner haben davor gewarnt, dass ‚ESG-Basic‘ zu einer neuen Greenwashing-Falle werden könnte.",
  },
];

const SOURCE_LINE = "Quelle: FNG-Marktbericht Nachhaltige Geldanlagen 2026. Stand: Stichtag 31.12.2025, veröffentlicht 17.06.2026.";

export default function MarktreportPage() {
  useEffect(() => {
    applySeo({
      title: "Nachhaltige Geldanlage Deutschland: Marktreport 2025/26",
      description: "FNG-Marktbericht 2026: 598,5 Mrd. € nachhaltige Geldanlagen in Deutschland (Stichprobe, Stand 31.12.2025), Waffenausschlüsse 62 % und weltweit erstmals Netto­abflüsse laut Morningstar.",
      canonicalPath: "/nachhaltige-geldanlage-deutschland",
    });
    // og:title / og:description / og:image seitenspezifisch setzen
    const setMeta = (sel, content) => {
      const m = document.querySelector(sel);
      if (m) m.setAttribute("content", content);
    };
    setMeta('meta[property="og:title"]', "Nachhaltige Geldanlage Deutschland: Marktreport 2025/26");
    setMeta('meta[property="og:description"]', "FNG-Bericht 2026: 598,5 Mrd. € Stichprobe, Waffenausschluss 62 %, Morningstar meldet weltweit −84 Mrd. USD Nettoabflüsse.");
    setMeta('meta[name="twitter:title"]', "Marktreport Nachhaltige Geldanlage Deutschland 2025/26");
    setMeta('meta[name="twitter:description"]', "Zahlen des FNG-Marktberichts 2026 – kompakt aufbereitet.");
  }, []);

  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Nachhaltige Geldanlage in Deutschland: Der Marktreport 2025/26",
    description: "Zahlen und Einordnungen aus dem FNG-Marktbericht 2026 (Stichtag 31.12.2025) für Privatanleger.",
    datePublished: "2026-09-20",
    dateModified: "2026-09-20",
    author: { "@type": "Organization", name: "Grüne Geldanlage" },
    publisher: { "@type": "Organization", name: "Grüne Geldanlage" },
    mainEntityOfPage: "https://gruene-geldanlage.com/nachhaltige-geldanlage-deutschland",
  };
  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="marktreport-page">
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }} />

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="overline mb-4">Marktreport · Deutschland</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6">
          Nachhaltige Geldanlage in Deutschland: Der Marktreport 2025/26.
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl leading-relaxed">
          Der FNG-Marktbericht 2026 erfasst zum Stichtag 31.12.2025 in Deutschland{" "}
          <strong>598,5 Milliarden Euro</strong> nachhaltige Geldanlagen bei einer Stichprobe
          aus 57 Instituten. Die Marktteilnehmer schließen weiterhin überwiegend Waffen aus,
          rund ein Drittel hat das ESG-Produktangebot 2025 ausgebaut. Global melden nachhaltige
          Fonds laut Morningstar zugleich erstmals Netto­abflüsse.
        </p>
        <p className="text-xs text-neutral-500 mt-5" data-testid="marktreport-date">
          Veröffentlicht 20.09.2026 · Stand der Daten: 31.12.2025
        </p>
      </section>

      {/* KPI CARDS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-14" data-testid="marktreport-kpis">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="border border-brand-primary bg-brand-primary text-white p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-white/80 mb-2">Erfasste nachhaltige Geldanlagen 2025</div>
            <div className="font-mono text-4xl md:text-5xl">{fmtVol(TOTAL_2025)} <span className="text-2xl md:text-3xl text-white/80">Mrd. €</span></div>
            <div className="text-sm text-white/80 mt-3">Deutschland, FNG-Stichprobe (57 Institute)</div>
          </div>
          <div className="border border-neutral-200 bg-white p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-primary mb-2">Waffenausschluss</div>
            <div className="font-mono text-4xl md:text-5xl text-brand-dark">62 %</div>
            <div className="text-sm text-neutral-600 mt-3">der erfassten Anlagen schließen sämtliche Waffen aus (2024: 72 %)</div>
          </div>
          <div className="border border-neutral-200 bg-white p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-primary mb-2">Produktangebot 2025</div>
            <div className="font-mono text-4xl md:text-5xl text-brand-dark">29 %</div>
            <div className="text-sm text-neutral-600 mt-3">der Institute haben ihr ESG-Angebot ergänzt, 1 % reduziert</div>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-4">{SOURCE_LINE}</p>
      </section>

      {/* AUF EINEN BLICK */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-glance">
        <div className="border-l-4 border-brand-primary bg-white p-8 md:p-10">
          <div className="overline mb-4">Auf einen Blick</div>
          <ul className="space-y-3 text-neutral-800 leading-relaxed">
            <li><strong>598,5 Mrd. €</strong> nachhaltige Geldanlagen in Deutschland (FNG-Stichprobe, Stichtag 31.12.2025); inklusive vergebener Kredite <strong>712 Mrd. €</strong>.</li>
            <li>In den Publikumsfonds sind <strong>94 %</strong> als Artikel 8, <strong>6 %</strong> als Artikel 9 klassifiziert – <strong>keine Artikel-6-Fonds</strong> in der Stichprobe.</li>
            <li>Weltweit erstmals <strong>Nettoabflüsse</strong> bei nachhaltigen Fonds und ETFs 2025 (−84 Mrd. US-Dollar laut Morningstar).</li>
            <li>Regulatorisch prägend: <strong>SFDR 2.0</strong> mit drei neuen Kategorien, <strong>CSRD-Omnibus</strong> und die <strong>ESMA-Leitlinien zu Fondsnamen</strong>.</li>
          </ul>
        </div>
      </section>

      {/* METHODIK-HINWEIS */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16">
        <div className="not-prose border-l-4 border-brand-accent bg-neutral-50 p-6 md:p-8" data-testid="method-note">
          <div className="flex items-start gap-3 mb-2">
            <AlertCircle size={20} strokeWidth={1.75} className="text-brand-accent shrink-0 mt-1" />
            <div className="overline">Methodik-Hinweis</div>
          </div>
          <p className="text-neutral-800 leading-relaxed">
            Die Volumina des FNG-Marktberichts 2026 (Stichtag 31.12.2025) sind{" "}
            <strong>nicht mit den Vorjahren vergleichbar</strong>: Die Teilnehmergruppe
            wechselt jährlich (Umfrage bei 77 Instituten, davon 57 in Deutschland). Wir setzen
            598,5 Mrd. € (2025) deshalb <strong>nicht in Relation</strong> zu 672,7 Mrd. €
            (2024) und verzichten auf Formulierungen wie „Rückgang" oder „Anstieg".
          </p>
        </div>
      </section>

      {/* DIE ZAHLEN 2025 IM DETAIL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8" data-testid="marktreport-details">
        <div className="max-w-4xl mx-auto">
          <div className="overline mb-3">Die Zahlen 2025 im Detail</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-4">
            Wie sich die 598,5 Milliarden Euro zusammensetzen
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-8">
            Die FNG-Stichprobe verteilt sich auf fünf Bereiche. Größter Block sind mit
            <strong> 156,8 Mrd. €</strong> die selbst­verwalteten Vermögen der Asset Owner
            (Versicherer, Pensions­kassen, Stiftungen). Knapp dahinter folgen{" "}
            <strong>nachhaltige Publikumsfonds</strong> mit 154,0 Mrd. €. <strong>Mandate und
            Spezialfonds</strong> (127,2 Mrd. €) sowie die <strong>Eigenanlagen der Banken</strong>{" "}
            (125,7 Mrd. €) liegen etwa gleichauf, während die <strong>Kundeneinlagen bei
            nachhaltigen Banken</strong> mit 34,8 Mrd. € den kleinsten Block bilden. Zusätzlich
            weist der Bericht 113 Mrd. € an vergebenen Krediten aus, die nicht in den
            598,5 Mrd. € enthalten sind – inklusive Kredite ergeben sich 712 Mrd. €.
          </p>
        </div>
      </section>

      {/* Composition chart + table */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-neutral-200 bg-white p-8 md:p-10">
            <div className="overline mb-2">Aufteilung 2025 (FNG-Stichprobe)</div>
            <h3 className="font-serif text-2xl md:text-3xl font-medium mb-8">Wo das nachhaltige Kapital in Deutschland liegt</h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={COMPOSITION_2025}
                    dataKey="vol"
                    nameKey="short"
                    outerRadius={95}
                    innerRadius={58}
                    label={({ share }) => `${share.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`}
                    labelLine={{ stroke: "#9CA3AF" }}
                  >
                    {COMPOSITION_2025.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, _, ctx) => [`${fmtVol(v)} Mrd. €`, ctx.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {COMPOSITION_2025.map((r) => (
                <div key={r.name} className="flex items-center gap-3 text-sm">
                  <span className="inline-block w-3 h-3 shrink-0" style={{ backgroundColor: r.color }} />
                  <span className="text-neutral-700 flex-1">{r.short}</span>
                  <span className="font-mono text-neutral-900">{fmtPct(r.share)}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500 mt-4">
              {SOURCE_LINE} Prozentanteile durch die Redaktion aus den Volumina berechnet.
            </p>
          </div>
          <div className="border border-neutral-200 bg-white p-8 md:p-10">
            <div className="overline mb-2">Volumina im Vergleich</div>
            <h3 className="font-serif text-2xl md:text-3xl font-medium mb-8">Wo die Milliarden liegen</h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={COMPOSITION_2025} layout="vertical" margin={{ top: 5, right: 40, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="short" width={170} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${fmtVol(v)} Mrd. €`} />
                  <Bar dataKey="vol" radius={[0, 2, 2, 0]}>
                    {COMPOSITION_2025.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    <LabelList dataKey="vol" position="right" formatter={(v) => `${fmtVol(v)}`} style={{ fontSize: 11, fill: "#374151" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-neutral-500 mt-4">{SOURCE_LINE}</p>
          </div>
        </div>

        {/* Table */}
        <div className="mt-8 bg-white border border-neutral-200 overflow-x-auto" data-testid="composition-table">
          <table className="w-full min-w-[640px]">
            <thead className="bg-neutral-50 border-b-2 border-brand-dark">
              <tr>
                <th className="text-left px-6 py-4 text-xs uppercase tracking-wider text-neutral-700 font-semibold">Bereich</th>
                <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-neutral-700 font-semibold">Volumen (Mrd. €)</th>
                <th className="text-right px-6 py-4 text-xs uppercase tracking-wider text-neutral-700 font-semibold">Anteil (berechnet)</th>
              </tr>
            </thead>
            <tbody>
              {COMPOSITION_2025.map((r) => (
                <tr key={r.name} className="border-b border-neutral-100">
                  <td className="px-6 py-4">
                    <span className="inline-block w-3 h-3 mr-3 align-middle" style={{ backgroundColor: r.color }} />
                    {r.name}
                  </td>
                  <td className="px-6 py-4 text-right font-mono">{fmtVol(r.vol)}</td>
                  <td className="px-6 py-4 text-right font-mono">{fmtPct(r.share)}</td>
                </tr>
              ))}
              <tr className="bg-brand-paper font-medium">
                <td className="px-6 py-4">Summe erfasste nachhaltige Geldanlagen</td>
                <td className="px-6 py-4 text-right font-mono">{fmtVol(TOTAL_2025)}</td>
                <td className="px-6 py-4 text-right font-mono">100,0 %</td>
              </tr>
              <tr className="border-t border-neutral-200 text-sm text-neutral-600">
                <td className="px-6 py-3">Zusätzlich vergebene Kredite (nicht in Summe enthalten)</td>
                <td className="px-6 py-3 text-right font-mono">{fmtVol(ADDITIONAL_CREDITS)}</td>
                <td className="px-6 py-3 text-right font-mono">—</td>
              </tr>
              <tr className="bg-neutral-50 font-medium">
                <td className="px-6 py-4">Inklusive Kredite</td>
                <td className="px-6 py-4 text-right font-mono">{fmtVol(TOTAL_INCL_CREDITS)}</td>
                <td className="px-6 py-4 text-right font-mono">—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Einordnung Artikel 8/9 */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16">
        <div className="prose-editorial max-w-none">
          <h2>Einordnung: Wie viel des Artikel-8/9-Marktes deckt der Bericht ab?</h2>
          <p>
            In der FNG-Stichprobe stecken <strong>273,5 Mrd. €</strong> in Fonds nach
            Artikel 8 und Artikel 9 der EU-Offenlegungs­verordnung. Laut BVI liegen die
            Artikel-8- und Artikel-9-Fonds in Deutschland im zweiten Halbjahr 2025 bei rund{" "}
            <strong>1.253 Mrd. €</strong> – der FNG-Bericht deckt damit etwa{" "}
            <strong>ein Fünftel</strong> des Marktes ab. Innerhalb der erfassten
            Publikumsfonds sind <strong>94 %</strong> als Artikel 8 und <strong>6 %</strong>{" "}
            als Artikel 9 klassifiziert; Artikel-6-Fonds (ohne Nachhaltigkeitsbezug) tauchen in
            der Stichprobe nicht auf. Die Kategorien sagen etwas über die regulatorische
            Selbst­einstufung aus – wenig aber über die tatsächliche Wirkung.
          </p>
        </div>
      </section>

      {/* Ausschlüsse 2024 vs 2025 */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-exclusions">
        <div className="border border-neutral-200 bg-white p-8 md:p-10">
          <div className="overline mb-2">Ausschlusskriterien Deutschland</div>
          <h3 className="font-serif text-2xl md:text-3xl font-medium mb-2">Ausschlüsse 2024 im Vergleich zu 2025</h3>
          <p className="text-neutral-700 leading-relaxed mb-6 max-w-3xl">
            Anteil der erfassten nachhaltigen Anlagen, die den jeweiligen Ausschluss anwenden.
            Das FNG kritisiert insbesondere die Lockerung bei Rüstung: Während in Österreich
            der Waffenausschluss von 78 % auf 86 % gestiegen ist, ging er in Deutschland von
            72 % auf 62 % zurück.
          </p>
          <div style={{ width: "100%", height: 340 }}>
            <ResponsiveContainer>
              <BarChart data={EXCLUSIONS} margin={{ top: 20, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="key" tick={{ fontSize: 10 }} interval={0} angle={-14} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v} %`} domain={[0, 100]} />
                <Tooltip formatter={(v) => `${v} %`} />
                <Bar dataKey="y2024" name="2024" fill="#A99968" radius={[2, 2, 0, 0]} />
                <Bar dataKey="y2025" name="2025" fill="#3E5743" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-6 mt-2 text-xs text-neutral-600">
            <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3" style={{ background: "#A99968" }} /> 2024</span>
            <span className="inline-flex items-center gap-2"><span className="inline-block w-3 h-3" style={{ background: "#3E5743" }} /> 2025</span>
          </div>
          <p className="text-[11px] text-neutral-500 mt-4">{SOURCE_LINE} · Werte 2024 aus FNG-Marktbericht 2025.</p>
        </div>
      </section>

      {/* Strategien */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-strategies">
        <div className="border border-neutral-200 bg-white p-8 md:p-10">
          <div className="overline mb-2">Anlagestrategien Deutschland 2025</div>
          <h3 className="font-serif text-2xl md:text-3xl font-medium mb-2">Was die Anbieter tatsächlich anwenden</h3>
          <p className="text-neutral-700 leading-relaxed mb-6 max-w-3xl">
            Anteil der erfassten Anlagen, die die jeweilige Strategie einsetzen. Klassische
            Ausschlüsse und normbasiertes Screening dominieren, Impact Investing bleibt eine
            kleine Nische.
          </p>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <BarChart data={STRATEGIES} layout="vertical" margin={{ top: 5, right: 40, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v} %`} />
                <YAxis type="category" dataKey="key" width={200} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v} %`} />
                <Bar dataKey="value" fill="#5C7A4E" radius={[0, 2, 2, 0]}>
                  <LabelList dataKey="value" position="right" formatter={(v) => `${v} %`} style={{ fontSize: 11, fill: "#374151" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-neutral-500 mt-4">{SOURCE_LINE}</p>
        </div>
      </section>

      {/* Asset-Allokation 2025 */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-allocation">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-neutral-200 bg-white p-8 md:p-10">
            <div className="overline mb-2">Asset-Allokation 2025</div>
            <h3 className="font-serif text-2xl md:text-3xl font-medium mb-8">Wie die Anlagen strukturiert sind</h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={ASSET_ALLOC}
                    dataKey="value"
                    nameKey="key"
                    outerRadius={95}
                    innerRadius={58}
                    label={({ value }) => `${value} %`}
                    labelLine={{ stroke: "#9CA3AF" }}
                  >
                    {ASSET_ALLOC.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, _, ctx) => [`${v} %`, ctx.payload.key]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {ASSET_ALLOC.map((r) => (
                <div key={r.key} className="flex items-center gap-3 text-sm">
                  <span className="inline-block w-3 h-3 shrink-0" style={{ backgroundColor: r.color }} />
                  <span className="text-neutral-700 flex-1">{r.key}</span>
                  <span className="font-mono text-neutral-900">{r.value} %</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500 mt-4">
              {SOURCE_LINE} Abweichende Zusammensetzung durch wechselnde Befragte – kein
              Vorjahresvergleich.
            </p>
          </div>

          {/* Branchenstimmung */}
          <div className="border border-neutral-200 bg-white p-8 md:p-10">
            <div className="overline mb-2">Branchenstimmung 2025/26 (n=73)</div>
            <h3 className="font-serif text-2xl md:text-3xl font-medium mb-4">Angebot und Wachstumserwartung</h3>
            <div className="mb-6">
              <div className="text-sm font-medium text-neutral-700 mb-2">Produktangebot 2025</div>
              <div className="space-y-2">
                {OFFER_CHANGE.map((r) => (
                  <div key={r.key} className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-neutral-700">{r.key}</span>
                      <span className="font-mono">{r.value} %</span>
                    </div>
                    <div className="h-2 bg-neutral-100"><div className="h-2 bg-brand-primary" style={{ width: `${r.value}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-neutral-700 mb-2">Wachstumserwartung 2026</div>
              <div className="space-y-2">
                {GROWTH_EXPECT_2026.map((r) => (
                  <div key={r.key} className="text-sm">
                    <div className="flex justify-between mb-1">
                      <span className="text-neutral-700">{r.key}</span>
                      <span className="font-mono">{r.value} %</span>
                    </div>
                    <div className="h-2 bg-neutral-100"><div className="h-2 bg-brand-accent" style={{ width: `${Math.max(r.value, 1)}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 mt-4">
              {SOURCE_LINE} Ambitionsniveau: 66 % unverändert, 23 % moderat erhöht, 8 %
              moderat gesenkt.
            </p>
          </div>
        </div>
      </section>

      {/* MORNINGSTAR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-morningstar">
        <div className="border border-neutral-200 bg-white p-8 md:p-10">
          <div className="overline mb-2">Globaler Kontext · Sekundärquelle</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
            Global erstmals Abflüsse: Was Morningstar zeigt
          </h2>
          <p className="text-neutral-700 leading-relaxed mb-8 max-w-3xl">
            Nachhaltige Fonds und ETFs weltweit verbuchten 2025 laut Morningstar
            Netto­abflüsse in Höhe von <strong>84 Mrd. US-Dollar</strong> – nach{" "}
            <strong>Zuflüssen von 38 Mrd. US-Dollar</strong> im Jahr 2024. Es ist das erste
            Jahr mit jährlichen Netto­abflüssen seit Beginn der Erhebung 2018 – und erstmals
            fließt Kapital auch aus Europa (rund <strong>−53 Mrd. €</strong>) sowie aus der
            Region außerhalb der USA ab. Das ist eine Aussage über Anlegerverhalten – kein
            Beleg für qualitativ schlechtere Produkte.
          </p>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={MORNINGSTAR} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}`} />
                <Tooltip formatter={(v) => `${v} Mrd. USD`} />
                <Bar dataKey="value" radius={[2, 2, 2, 2]} barSize={80}>
                  {MORNINGSTAR.map((d, i) => (
                    <Cell key={i} fill={d.value >= 0 ? "#3E5743" : "#8B5E3C"} />
                  ))}
                  <LabelList dataKey="value" position="top" formatter={(v) => `${v > 0 ? "+" : ""}${v} Mrd. USD`} style={{ fontSize: 11, fill: "#374151" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-neutral-500 mt-4">
            laut Morningstar, wiedergegeben nach Vividam/Cash. Stand: Stichtag 31.12.2025.
          </p>
        </div>
      </section>

      {/* RÜCKBLICK 2019–2024 (frühere FNG-Erhebung, andere Stichprobe) */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-history">
        <div className="border border-dashed border-neutral-300 bg-white p-8 md:p-10">
          <div className="overline mb-2">Rückblick: Die FNG-Zahlen bis 2024</div>
          <h2 className="font-serif text-3xl md:text-4xl font-medium mb-2">Volumen-Entwicklung 2019 bis 2024</h2>
          <p className="text-sm text-neutral-600 mb-6 max-w-3xl">
            <strong>Hinweis zur Vergleichbarkeit:</strong> Frühere Erhebungen des FNG,
            <strong> andere Stichprobe. Nicht mit dem Bericht 2026 vergleichbar.</strong> Die
            hier gezeigten Werte stammen aus vorangegangenen FNG-Marktberichten; der Wert
            für 2024 aus dem FNG-Marktbericht 2025.
          </p>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={HIST_TIMESERIES} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}`} />
                <Tooltip formatter={(v) => `${fmtVol(v)} Mrd. €`} labelFormatter={(l) => `Jahr ${l}`} />
                <Line type="monotone" dataKey="vol" stroke="#3E5743" strokeWidth={2.5} dot={{ r: 4, fill: "#3E5743" }} />
                <ReferenceDot x={2023} y={543} r={6} fill="#8B5E3C" stroke="#fff" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-8 max-w-3xl">
            <h3 className="font-serif text-xl font-medium mb-3">Der Markt in Zahlen</h3>
            <p className="text-neutral-700 leading-relaxed mb-4">
              In den früheren FNG-Erhebungen bis 2024 stieg das erfasste Gesamtvolumen
              nachhaltiger Geldanlagen in Deutschland von rund <strong>269 Mrd. €</strong> (2019)
              auf <strong>672,7 Mrd. €</strong> (2024). Diese Zahlen sind – wegen wechselnder
              Teilnehmergruppen – nicht mit dem Wert 2025 vergleichbar.
            </p>
            <h3 className="font-serif text-xl font-medium mb-3">Wer bewegte das Kapital?</h3>
            <p className="text-neutral-700 leading-relaxed mb-4">
              In der Erhebung 2024 dominierten institutionelle Vehikel: Nachhaltige
              Spezialfonds und Mandate machten rund 46,5 %, nachhaltige Publikumsfonds
              rund 35,9 %, Kundeneinlagen und Eigenanlagen nachhaltiger Banken rund
              17,6 % des damals erfassten Volumens aus.
            </p>

            <div className="not-prose my-6 border-l-4 border-brand-accent bg-neutral-50 p-6" data-testid="market-2023-note">
              <div className="flex items-start gap-3 mb-2">
                <AlertCircle size={20} strokeWidth={1.75} className="text-brand-accent shrink-0 mt-1" />
                <div className="overline">Kontext zum Rückgang 2023 (Einordnung)</div>
              </div>
              <p className="text-neutral-800 leading-relaxed">
                Laut FNG-Berichten trugen 2023 methodische Anpassungen (strengere Kriterien
                zur Klassifizierung nachhaltiger Strategien) sowie die geldpolitische
                Zinswende zur niedriger ausgewiesenen Volumensumme bei. Die genannten
                Ursachen sind <strong>Einordnungen der Berichte</strong>, keine kausalen
                Belege.
              </p>
            </div>
          </div>
          <p className="text-[11px] text-neutral-500 mt-4">
            Quelle Rückblick: Forum Nachhaltige Geldanlagen e.V., frühere Marktberichte;
            2024er Werte aus FNG-Marktbericht 2025.
          </p>
        </div>
      </section>

      {/* AUSBLICK & PRIVATANLEGER */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16">
        <div className="prose-editorial max-w-none">
          <h2>Ausblick: SFDR 2.0, CSRD-Omnibus, ESMA-Namen</h2>
          <p>
            Der Regulierungs­rahmen ist in Bewegung. Die Reform der EU-Offenlegungs­verordnung –
            <strong> SFDR 2.0</strong> – ersetzt die bisherigen Artikel-8- und -9-Klassifikationen
            durch drei neue Kategorien: <strong>„Transition"</strong>, <strong>„ESG-Basic"</strong>{" "}
            und <strong>„Sustainable"</strong>. Eine erste, vom FNG zitierte Schätzung geht davon
            aus, dass <strong>rund zwei Drittel</strong> der bestehenden ESG-Fonds in die
            Kategorie „ESG-Basic" fallen könnten. Mehr als 130 Unterzeichner warnen dort vor
            einem neuen <strong>Greenwashing-Risiko</strong>: 46 % der Befragten erwarten von
            SFDR 2.0 positive Impulse, 18 % nicht. Details lesen Sie in unserem Artikel{" "}
            <Link to="/artikel/sfdr-2-0-reform">SFDR 2.0 – Reform der EU-Offenlegungs­verordnung</Link>.
          </p>
          <p>
            Zeitgleich reduziert der <strong>CSRD-Omnibus</strong> die Zahl der berichts­pflichtigen
            Unternehmen um rund 80 %. Und die <strong>ESMA-Leitlinien zu Fondsnamen</strong>{" "}
            wirken bereits: 59 % der befragten Anbieter haben ihre Anlagestrategie angepasst,
            10 % den Fondsnamen. Wie sich Greenwashing dennoch erkennen lässt, zeigt unser
            Praxisartikel <Link to="/artikel/greenwashing-erkennen">Greenwashing erkennen</Link>.
          </p>

          <h2>Was das für Privatanleger bedeutet</h2>
          <p>
            Für Privatanleger sind vor allem die <strong>154,0 Milliarden Euro in nachhaltigen
            Publikumsfonds</strong> (FNG-Stichprobe 2025) die praktisch relevante Größe – hier
            bewegt sich das Kapital, das über Sparpläne, ETFs und aktiv gemanagte Fonds
            zugänglich ist. Wichtig: Die Kategorien Artikel 8 und 9 sagen wenig über die
            tatsächliche Wirkung eines Fonds; die anstehende Neuordnung durch SFDR 2.0 wird
            die Auswahl noch einmal erweitern – und komplexer machen. Dieser Report ist keine
            Anlage­beratung. Wer die tatsächliche Wirkung des eigenen Kapitals abschätzen
            möchte, findet in unseren Werkzeugen{" "}
            <Link to="/rechner/co2-fussabdruck">CO₂-Fußabdruck</Link> und{" "}
            <Link to="/rechner/gruener-hebel">Grüner Hebel</Link> konkrete Rechen­hilfen. Details
            zum FNG-Bericht selbst finden Sie in unserem Beitrag{" "}
            <Link to="/artikel/fng-marktbericht-2026">FNG-Marktbericht 2026</Link>. Wie sich der
            Waffenausschluss und die Rolle von Rüstungswerten in ESG-Fonds entwickeln, ordnen
            wir separat ein: <Link to="/artikel/ruestungsaktien-esg-fonds">Rüstungsaktien in
            ESG-Fonds</Link>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-faq">
        <div className="overline mb-3">Häufige Fragen</div>
        <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-8">FAQ zum Marktreport</h2>
        <div className="space-y-4">
          {FAQ.map((f, i) => (
            <details key={i} className="group border border-neutral-200 bg-white p-6 open:border-brand-primary">
              <summary className="cursor-pointer font-serif text-lg font-medium text-brand-dark list-none flex justify-between items-center">
                <span>{f.q}</span>
                <span className="text-brand-primary group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-neutral-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* RISIKOHINWEIS */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16">
        <div className="border border-neutral-200 bg-neutral-50 p-6 flex items-start gap-4" data-testid="risikohinweis-box">
          <ShieldAlert size={22} strokeWidth={1.75} className="text-brand-primary shrink-0 mt-1" />
          <div className="text-sm text-neutral-700 leading-relaxed">
            <strong>Risikohinweis:</strong> Dieser Marktreport dient ausschließlich der
            Information und stellt keine Anlage­beratung, Kauf- oder Verkaufs­empfehlung dar.
            Kapital­anlagen sind mit Risiken verbunden – bis hin zum vollständigen Kapital­verlust.
            Bitte beachten Sie unseren ausführlichen <Link to="/risikohinweis" className="text-brand-primary underline">Risikohinweis</Link>.
          </div>
        </div>
      </section>

      {/* DATENQUELLEN */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-16" data-testid="marktreport-sources">
        <h2 className="font-serif text-2xl md:text-3xl font-medium mb-4">Datenquellen</h2>
        <ul className="text-sm text-neutral-700 space-y-2 leading-relaxed">
          <li>
            Forum Nachhaltige Geldanlagen e.V.:{" "}
            <a href="https://www.forum-ng.org/fileadmin/Marktbericht/2026/FNG_Marktbericht2026_Web.pdf" target="_blank" rel="noopener external" className="text-brand-primary underline">
              Marktbericht Nachhaltige Geldanlagen 2026 (PDF)
            </a>{" "}
            – Stichtag 31.12.2025, veröffentlicht 17.06.2026.
          </li>
          <li>
            Cash.:{" "}
            <a href="https://www.cash-online.de/a/fng-marktbericht-2026-nachhaltige-geldanlagen-trotzen-politischem-gegenwind-720368/" target="_blank" rel="noopener external" className="text-brand-primary underline">
              FNG-Marktbericht 2026: Nachhaltige Geldanlagen trotzen politischem Gegenwind
            </a>.
          </li>
          <li>
            Vividam:{" "}
            <a href="https://www.vividam.de/fng-marktbericht-2026-nachhaltige-geldanlagen/" target="_blank" rel="noopener external" className="text-brand-primary underline">
              FNG-Marktbericht 2026 – Nachhaltige Geldanlagen
            </a>{" "}
            (Wiedergabe der Morningstar-Zahlen).
          </li>
          <li>
            Historische Werte 2019–2024: Forum Nachhaltige Geldanlagen e.V.,{" "}
            <a href="https://www.forum-ng.org/de/veroffentlichungen/marktberichte.html" target="_blank" rel="noopener external" className="text-brand-primary underline">
              FNG-Marktbericht 2025 und frühere Ausgaben
            </a>.
          </li>
        </ul>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-neutral-200 py-16" data-testid="marktreport-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-medium mb-6">Vertiefen Sie das Thema</h2>
          <p className="text-neutral-600 mb-8 max-w-2xl mx-auto">
            Weiterführende Analysen, Grundlagenartikel und praktische Prüfanleitungen in
            unserer Blog-Übersicht.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-brand-primary text-white px-8 py-4 text-sm font-medium tracking-wide no-underline hover:bg-brand-hover transition-colors"
          >
            Alle Artikel in der Übersicht <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
